import { createContext, useContext } from 'react';
import type { DateTime } from 'luxon';

/**
 * Drag mechanism for the calendar (plan 0017 §3, P3.S1 + the P3.S1 follow-up
 * gripp-uzs.15). Web-only, pointer-based: a long-press on an EventPill picks it
 * up; moving the pointer hit-tests against the drop targets to highlight the cell
 * under it; releasing computes the new date and fires `onEventDrop`. No mutation
 * here — the screen owns persistence (P3.S2 wires `useUpdateIssue`).
 *
 * Live geometry (gripp-uzs.15 fix): targets expose a `getRect()` that reads
 * `getBoundingClientRect()` at hit-test time, NOT a rect cached at onLayout.
 * Cached rects went stale behind SlideLayout — the left column animates in with a
 * `translateX`, so cells measured during/after the slide-in cached offset coords
 * and the pointer never fell inside any cached rect. `getBoundingClientRect`
 * always reflects the current transform + scroll, so it survives transitions,
 * scroll, and nav re-layout. Because it is viewport-relative, all pointer coords
 * flowing through the controller are CLIENT coords (clientX/clientY), not page.
 *
 * §6.1 fidelity note: gripp-OS uses the HTML5 drag-and-drop API (native drag
 * image, dragover/drop events). RN primitives don't expose HTML5 DnD, so this is
 * a pointer/long-press reimplementation: press-and-hold to pick up (no instant
 * grab), no OS drag-image (the origin pill dims instead), targets hit-tested
 * against live rects rather than browser-fired. Outcome matches; feel is close.
 */

/** A viewport-relative rect (the subset of DOMRect we hit-test against). */
export interface ClientRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * A registered drop target: its calendar identity + a live-geometry accessor.
 * `getRect()` returns the target's current viewport rect (or null if the node is
 * gone), read fresh at hit-test time so transforms/scroll never make it stale.
 */
export interface DropTarget {
  /** Live viewport rect of the cell/slot, or null if unmounted. */
  getRect: () => ClientRect | null;
  /** Month-cell grid index (0–41), present for the month grid. */
  index?: number;
  /** Week-slot hour, present for the week grid. */
  hour?: number;
  /** The day this target represents (both grids). */
  day: DateTime;
}

/** Registry key: `m:<index>` for month cells, `w:<col>-<hour>` for week slots. */
export type DropTargetKey = string;

export const monthTargetKey = (index: number): DropTargetKey => `m:${index}`;
export const weekTargetKey = (colIndex: number, hour: number): DropTargetKey =>
  `w:${colIndex}-${hour}`;

/**
 * Context the grids/pills use to talk to the drag controller in the Calendar
 * container. Undefined when the calendar isn't draggable (no provider mounted),
 * in which case pills stay click-to-open only. All coords are CLIENT coords.
 */
export interface CalendarDragContextValue {
  /** True when drag is enabled (the `draggable` prop). */
  enabled: boolean;
  /** Register/update a drop target (called from a cell's ref callback). */
  registerTarget: (key: DropTargetKey, target: DropTarget) => void;
  /** Remove a target (cell unmounted / re-rendered out). */
  unregisterTarget: (key: DropTargetKey) => void;
  /** Begin dragging an event from a client point. */
  startDrag: (eventId: string, at: DateTime, clientX: number, clientY: number) => void;
  /** Update the dragged pointer position (drives drop-target highlight). */
  updateDrag: (clientX: number, clientY: number) => void;
  /** Finish the drag at a client point (computes + emits the drop, or cancels). */
  endDrag: (clientX: number, clientY: number) => void;
  /** Cancel an in-flight drag with no drop. */
  cancelDrag: () => void;
  /** The drop-target key currently under the pointer (for highlight), or null. */
  activeDropKey: DropTargetKey | null;
  /** The event id currently being dragged (origin pill lifts), or null. */
  draggingId: string | null;
}

export const CalendarDragContext = createContext<CalendarDragContextValue | undefined>(
  undefined,
);

export const useCalendarDrag = (): CalendarDragContextValue | undefined =>
  useContext(CalendarDragContext);

/** Hit-test a client point against a viewport rect (inclusive). */
export const pointInRect = (rect: ClientRect, x: number, y: number): boolean =>
  x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;

/**
 * Compute the new event date for a drop (gripp-OS `onAdjustDate` semantics):
 *   • month → keep the event's time-of-day, set the target day's date.
 *   • week  → set the target day AND the dropped hour (minute preserved).
 */
export const computeDroppedDate = (eventAt: DateTime, target: DropTarget): DateTime => {
  if (target.hour !== undefined) {
    // Week slot: take the target day, set the dropped hour, keep the minute.
    return target.day.set({ hour: target.hour, minute: eventAt.minute, second: 0, millisecond: 0 });
  }
  // Month cell: take the target day, preserve the event's time-of-day.
  return target.day.set({
    hour: eventAt.hour,
    minute: eventAt.minute,
    second: 0,
    millisecond: 0,
  });
};
